export type Position = [number, number];

export type Geometry = Point | MultiPoint | LineString | MultiLineString | Polygon | MultiPolygon;

export interface Point {
    readonly type: 'Point';
    readonly coordinates: Position;
}

export interface MultiPoint {
    readonly type: 'MultiPoint';
    readonly coordinates: Position[];
}

export interface LineString {
    readonly type: 'LineString';
    readonly coordinates: Position[];
}

export interface MultiLineString {
    readonly type: 'MultiLineString';
    readonly coordinates: Position[][];
}

export interface Polygon {
    readonly type: 'Polygon';
    readonly coordinates: Position[][];
}

export interface MultiPolygon {
    readonly type: 'MultiPolygon';
    readonly coordinates: Position[][][];
}

export const isPoint = (geometry: Geometry): geometry is Point => geometry.type === 'Point';
export const isMultiPoint = (geometry: Geometry): geometry is MultiPoint => geometry.type === 'MultiPoint';
export const isLineString = (geometry: Geometry): geometry is LineString => geometry.type === 'LineString';
export const isMultiLineString = (geometry: Geometry): geometry is MultiLineString => geometry.type === 'MultiLineString';
export const isPolygon = (geometry: Geometry): geometry is Polygon => geometry.type === 'Polygon';
export const isMultiPolygon = (geometry: Geometry): geometry is MultiPolygon => geometry.type === 'MultiPolygon';

const TOLERANCE = 0.00001;


const pointInPoint = (p1: Position, p2: Position, t = TOLERANCE) => Math.abs(p1[0] - p2[0]) <= t && Math.abs(p1[1] - p2[1]) <= t;


const intersect = (point: Readonly<Position>, linePoint1: Readonly<Position>, linePoint2: Readonly<Position>) => {
    const [x, y] = point;
    let [x1, y1] = linePoint1;
    let [x2, y2] = linePoint2;

    // if first line point is greater then second, swap points for easier calculation
    if (y1 > y2) {
        [x1, y1, x2, y2] = [x2, y2, x1, y1];
    }

    if (y <= y1 || y > y2 || (x >= x1 && x >= x2)) {
        return false;
    } else if (x < x1 && x < x2) {
        return true;
    } else {
        return (y - y1) / (x - x1) > (y2 - y1) / (x2 - x1);
    }
};

/**
 * line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
 * Determine if two line segment intersects
 */
const lineIntersect = (line1Point1: Position, line1Point2: Position, line2Point1: Position, line2Point2: Position) => {
    const [x1, y1] = line1Point1;
    const [x2, y2] = line1Point2;
    const [x3, y3] = line2Point1;
    const [x4, y4] = line2Point2;

    // Check if none of the lines are of length 0
    if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
        return false;
    }

    const denominator = ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));

    // Lines are parallel
    if (denominator === 0) {
        return false;
    }

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

    // is the intersection along the segments
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
        return false;
    }

    return true;
};

/**
 * ray casting algorithm for determining if point is inside a polygon without holes
 */
const pointInRing = (point: Readonly<Position>, ring: Readonly<Position[]>) => {
    let count = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        if (intersect(point, ring[i], ring[j])) {
            count++;
        }
    }
    return count % 2 !== 0;
};

export const pointInPolygon = (point: Readonly<Position>, polygon: Readonly<Position[][]>, tolerance = TOLERANCE) => {
    // check outer ring
    if (!pointInRing(point, polygon[0])) {
        return false;
    }
    // check holes
    for (const hole of polygon.slice(1)) {
        if (pointInRing(point, hole)) {
            return false;
        }
    }
    return true;
};

const multiPolygonStartsInMultiPolygon = (contained: MultiPolygon, containing: MultiPolygon) => {
    for (const containedElement of contained.coordinates) {
        for (const containingElement of containing.coordinates) {
            if (pointInPolygon(containedElement[0][0], containingElement)) {
                return true;
            }
        }
    }
    return false;
};

// distance between two geographical points using spherical law of cosines approximation
// see http://rosettacode.org/wiki/Haversine_formula
export const distanceBetweenPoints = (point1: Position, point2: Position) => {
    // constant to get result in meters
    const R = 6371000;

    const rad = Math.PI / 180;
    const lat1 = point1[1] * rad;
    const lat2 = point2[1] * rad;
    const sinDLat = Math.sin((point2[1] - point1[1]) * rad / 2);
    const sinDLon = Math.sin((point2[0] - point1[0]) * rad / 2);
    const a = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const pointDistance = (a: Position, b: Position) => {
    return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);
};

const pointInLine = (point: Position, line: Position[], tolerance = TOLERANCE) => {
    for (let i = 0; i < line.length - 1; i++) {
        const c = point;
        const a = line[i];
        const b = line[i + 1];
        const distance = Math.abs((pointDistance(a, c) + pointDistance(c, b) - pointDistance(a, b)));
        if (distance <= tolerance) {
            return true;
        }
    }
    return false;
};


const checkMultiGeometry = <P, G, T>(point: P, geometries: G[], tolerance: T, predicate: (p: P, g: G, t: T) => boolean) =>
    geometries.some(geometry => predicate(point, geometry, tolerance));


export const pointInGeometry = (point: Position, geometry: Geometry, tolerance = TOLERANCE) => {
    switch (geometry.type) {
        case 'Point':
            return pointInPoint(point, geometry.coordinates, tolerance);
        case 'Polygon':
            return pointInPolygon(point, geometry.coordinates, tolerance);
        case 'LineString':
            return pointInLine(point, geometry.coordinates, tolerance);
        case 'MultiPoint':
            return checkMultiGeometry(point, geometry.coordinates, tolerance, pointInPoint);
        case 'MultiPolygon':
            return checkMultiGeometry(point, geometry.coordinates, tolerance, pointInPolygon);
        case 'MultiLineString':
            return checkMultiGeometry(point, geometry.coordinates, tolerance, pointInLine);
    }
    return false;
};

export const boundingBox = (geometries: Geometry[]): [Position, Position] => {
    if (geometries.length === 0) {
        throw Error('Empty geometries');
    }

    const points: Position[] = geometries.flatMap(geometry => {
        switch (geometry.type) {
            case 'Point':
                return [geometry.coordinates];
            case 'Polygon':
                return geometry.coordinates[0];
            case 'LineString':
                return geometry.coordinates;
            case 'MultiPoint':
                return geometry.coordinates;
            case 'MultiPolygon':
                return geometry.coordinates.flatMap(polygon => polygon[0]);
            case 'MultiLineString':
                return geometry.coordinates.flatMap(lineString => lineString);
            default:
                throw Error('Unreachable');
        }
    });

    return [
        [min(points.map(p => p[0])), min(points.map(p => p[1]))],
        [max(points.map(p => p[0])), max(points.map(p => p[1]))],
    ];
};

export const min = (arr: number[]) => {
    return arr.reduce((a, b) => (b < a ? b : a));
};

export const max = (arr: number[]) => {
    return arr.reduce((a, b) => (b > a ? b : a));
};

export const getLinesFromRing = (ring: Position[]) => {
    const lines: Position[][] = [];
    let lastPoint: Position;
    let firstPoint: Position;

    ring.forEach(point => {
        if (!lastPoint) {
            lastPoint = point;
            firstPoint = point;
            return;
        }
        lines.push([lastPoint, point]);
        lastPoint = point;

        if (ring[ring.length - 1] === point) {
            lines.push([point, firstPoint]);
        }
    });

    return lines;
};

export const polygonIntersect = (polygon1: Polygon | MultiPolygon, polygon2: Polygon | MultiPolygon) => {
    const mPolygonA: Geometry = isMultiPolygon(polygon1) ? polygon1 : { type: 'MultiPolygon', coordinates: [polygon1.coordinates] };
    const mPolygonB: Geometry = isMultiPolygon(polygon2) ? polygon2 : { type: 'MultiPolygon', coordinates: [polygon2.coordinates] };

    const linesA = mPolygonA.coordinates.flatMap(polyInA => getLinesFromRing(polyInA[0]));
    const linesB = mPolygonB.coordinates.flatMap(polyInB => getLinesFromRing(polyInB[0]));


    for (const line1 of linesA) {
        for (const line2 of linesB) {
            if (lineIntersect(line1[0], line1[1], line2[0], line2[1])) {
                return true;
            }
        }
    }

    return multiPolygonStartsInMultiPolygon(mPolygonA, mPolygonB)
        || multiPolygonStartsInMultiPolygon(mPolygonB, mPolygonA);
};

export const getCenterOfPoints = (point1: Position, point2: Position) => {
    const midLat = (point1[0] + point2[0]) / 2;
    const midLng = (point1[1] + point2[1]) / 2;
    return [midLat, midLng];
};

export const multiPointToPoints = (geometry: MultiPoint): Point[] => {
    return geometry.coordinates.map(position => ({
        type: 'Point',
        coordinates: position,
    }));
};
